<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Header -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add Family Member</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Add a family member linked to an existing customer account
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-account-multiple</v-icon>
                      Family Member
                    </v-chip>
                    <v-btn color="grey darken-1" outlined @click="$router.push('/patients')" class="back-button">
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Patients
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-form ref="form" v-model="valid" @submit.prevent="savePatient">
              <v-card-text class="pa-4">

                <!-- Parent Patient -->
                <div class="form-section mb-4">
                  <h3 class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-account-supervisor</v-icon>
                    Parent Customer
                  </h3>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.parent_patient_id"
                        :items="parentPatientItems"
                        item-text="label"
                        item-value="id"
                        label="Select Customer *"
                        prepend-inner-icon="mdi-account-search"
                        outlined
                        dense
                        clearable
                        :loading="loadingParents"
                        :rules="[v => !!v || 'Please select a customer']"
                        no-data-text="No customers found"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.relation"
                        :items="relationItems"
                        item-text="text"
                        item-value="value"
                        label="Relation *"
                        prepend-inner-icon="mdi-account-heart"
                        outlined
                        dense
                        clearable
                        :rules="[v => !!v || 'Relation is required']"
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="mb-4"></v-divider>

                <!-- Basic Information -->
                <div class="form-section mb-4">
                  <h3 class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Basic Information
                  </h3>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        prepend-inner-icon="mdi-account"
                        outlined dense
                        :rules="[v => !!v || 'First name is required']"
                        :error-messages="errors.first_name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.gender_id"
                        :items="genderItems"
                        item-text="text"
                        item-value="value"
                        label="Gender"
                        prepend-inner-icon="mdi-gender-male-female"
                        outlined dense clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-menu
                        v-model="dobMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.dob"
                            label="Date of Birth"
                            prepend-inner-icon="mdi-calendar"
                            outlined dense readonly clearable
                            v-bind="attrs" v-on="on"
                            :error-messages="errors.dob"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.dob"
                          no-title
                          :max="new Date().toISOString().substr(0, 10)"
                          @input="dobMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.bloodgroup_id"
                        :items="bloodGroupItems"
                        item-text="text"
                        item-value="value"
                        label="Blood Group"
                        prepend-inner-icon="mdi-water"
                        outlined dense clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.diet_id"
                        :items="dietItems"
                        item-text="text"
                        item-value="value"
                        label="Diet"
                        prepend-inner-icon="mdi-food-apple"
                        outlined dense clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="mb-4"></v-divider>

                <!-- Physical Details -->
                <div class="form-section mb-4">
                  <h3 class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-human-male-height</v-icon>
                    Physical Details
                  </h3>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.weight"
                        label="Weight (kg)"
                        prepend-inner-icon="mdi-weight-kilogram"
                        outlined dense type="number"
                        :error-messages="errors.weight"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.height"
                        label="Height (cm)"
                        prepend-inner-icon="mdi-human-male-height"
                        outlined dense type="number"
                        :error-messages="errors.height"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="mb-4"></v-divider>

                <!-- Preferred Languages -->
                <div class="form-section mb-4">
                  <h3 class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-translate</v-icon>
                    Preferred Languages
                  </h3>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.preferred_language_ids"
                        :items="languageItems"
                        item-text="text"
                        item-value="value"
                        label="Preferred Languages"
                        prepend-inner-icon="mdi-translate"
                        outlined dense clearable multiple chips small-chips deletable-chips
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="mb-4"></v-divider>

                <!-- Account Settings -->
                <div class="form-section mb-4">
                  <h3 class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Account Settings
                  </h3>
                  <v-row>
                    <v-col cols="12" md="6" class="d-flex align-center">
                      <v-switch v-model="form.is_active" color="success" class="mt-0">
                        <template #label>
                          <span>Account Status: </span>
                          <v-chip x-small :color="form.is_active ? 'success' : 'grey'" dark class="ml-2">
                            {{ form.is_active ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </template>
                      </v-switch>
                    </v-col>
                  </v-row>
                </div>

              </v-card-text>

              <!-- Footer -->
              <v-divider></v-divider>
              <v-card-actions class="px-4 py-3 justify-end">
                <v-chip v-if="valid" color="success" outlined x-small class="mr-3">
                  <v-icon left x-small>mdi-check-circle</v-icon>
                  Form Valid
                </v-chip>
                <v-btn outlined @click="$router.push('/patients')" class="mr-2">Cancel</v-btn>
                <v-btn color="success" type="submit" :loading="isLoading" :disabled="!valid">
                  <v-icon left small>mdi-content-save</v-icon>
                  Save Patient
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";

export default {
  name: "PatientCreatePage",
  data() {
    return {
      valid: false,
      form: {
        first_name: "",
        gender_id: null,
        dob: "",
        bloodgroup_id: null,
        diet_id: null,
        weight: "",
        height: "",
        parent_patient_id: null,
        relation: "",
        preferred_language_ids: [],
        position_id: null,
        is_active: true,
        role_id: "3",
        soft_password: "123456",
      },
      errors: {},
      isLoading: false,
      loadingParents: false,
      dobMenu: false,
      genderItems: [],
      bloodGroupItems: [],
      dietItems: [],
      languageItems: [],
      parentPatientItems: [],
      relationItems: [],
    };
  },
  computed: {
    ...mapGetters({ company: "auth/company" }),
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=patient`);
        const masters = response.data;

        const patientPosition = masters.positions?.find(p => p.name.toUpperCase() === 'PATIENT');
        if (patientPosition) this.form.position_id = patientPosition.id;

        this.loadParentPatients();

        if (masters.genders) {
          this.genderItems = masters.genders.map(g => ({ text: g.description, value: g.id }));
        }
        if (masters.blood_groups) {
          this.bloodGroupItems = masters.blood_groups.map(b => ({ text: b.description, value: b.id }));
        }
        if (masters.diets) {
          this.dietItems = masters.diets.map(d => ({ text: d.description, value: d.id }));
        }
        if (masters.languages) {
          this.languageItems = masters.languages.map(l => ({ text: l.name, value: l.id }));
        }
        if (masters.relations) {
          this.relationItems = masters.relations.map(r => ({ text: r.text, value: r.value }));
        }
      } catch (error) {
        console.error(error);
      }
    },

    async loadParentPatients() {
      try {
        this.loadingParents = true;
        const posId = this.form.position_id ? `&position_id=${this.form.position_id}` : '';
        const res = await axios.get(`users?parent_patient_id=null&rowsPerPage=500&page=1${posId}`);
        this.parentPatientItems = (res.data.data || []).map(u => ({
          id: u.id,
          label: `${u.user_name || (u.first_name + ' ' + (u.last_name || '')).trim()}${u.phone ? ' • ' + u.phone : ''}`,
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingParents = false;
      }
    },

    async savePatient() {
      this.$refs.form.validate();
      if (!this.valid) return;

      this.isLoading = true;
      try {
        const payload = {
          first_name: this.form.first_name,
          gender_id: this.form.gender_id,
          dob: this.form.dob || null,
          bloodgroup_id: this.form.bloodgroup_id,
          diet_id: this.form.diet_id,
          weight: this.form.weight || null,
          height: this.form.height || null,
          parent_patient_id: this.form.parent_patient_id,
          relation: this.form.relation,
          preferred_language_ids: this.form.preferred_language_ids,
          position_id: this.form.position_id,
          is_active: this.form.is_active,
          role_id: this.form.role_id,
          soft_password: this.form.soft_password,
        };

        await axios.post("/patient/create", payload);
        this.$toast.success("Family member added successfully");
        this.$router.push("/patients");
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$toast.error("Failed to add family member");
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
}

.page-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
}

.page-subtitle {
  font-size: 0.78rem;
}

.back-button {
  font-size: 0.75rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #424242;
  display: flex;
  align-items: center;
}
</style>
